import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as expertInquirySelectors from "@rentiohq/shared-frontend/dist/redux/expertInquiry/expertInquiry.selectors";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  Modal,
  MultiStepForm,
  Select,
  SuccessModal,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import orderLocationDescriptionSchemas from "../../forms/orderLocationDescription";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  performingSubmitActionResultSelector,
  submitActionCreator,
} from "../../forms/orderLocationDescription/schema.orderLocationDescription.actions";
import { EField } from "../../forms/orderLocationDescription/schema.orderLocationDescription.types";
import { ts } from "../../services";
import { RequestRentioMoveServiceModal } from "../RequestRentioMoveServiceModal";
import { EModals } from "./LocationDescriptionModal.types";
import { genInitialModal } from "./LocationDescriptionModal.utils";

interface ILocationDescriptionModalProps {
  property: IProperty;
  contract: IContract;
  onClose: (task?: any) => void;
}

const formId = generateFormId();

export const LocationDescriptionModal: FC<
  ILocationDescriptionModalProps
> = props => {
  const { property, contract, onClose } = props;

  const partnerships = useSelector((state: IRootStore) =>
    expertInquirySelectors.partnerships(state),
  );

  const hasRentioMoveServiceEnabled = useBrokerFeature(
    EBrokerFeature.RentioMove,
  );

  const [energyTransferByMexp, setEnergyTransferByMexp] = useState(false);
  const [selectedPartnershipAccountId, setSelectedPartnershipAccountId] =
    useState<number | undefined>();

  const createdTaskId = useSelector(
    (state: IRootStore) =>
      state.expertInquiry.requestedLocationDescriptionTaskId,
  );

  const [currentModal, setCurrentModal] = useState<EModals | undefined>(
    genInitialModal(partnerships),
  );

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (!partnerships) {
      return;
    }

    setSelectedPartnershipAccountId(partnerships[0]?.partnerAccountId);
  }, [partnerships]);

  const selectedPartnership =
    partnerships?.find(
      x => x.partnerAccountId === selectedPartnershipAccountId,
    ) || partnerships?.[0];

  // Event handlers
  const handlePriceListClick = () => {
    utils.general.openWindow(ts.locationDescriptionPricesLink());
  };

  const handleSuccess = (formData: any) => {
    setEnergyTransferByMexp(formData[EField.EnergyTransferByMexp] === "yes");
    setCurrentModal(EModals.Outro);
  };

  const handleCloseOutroModal = () => {
    if (energyTransferByMexp) {
      setCurrentModal(EModals.EnergyTransfer);
      return;
    }

    return onClose();
  };

  // Render functions
  const renderPartnerPickerModal = () => {
    return (
      <Modal
        width="medium"
        onClose={handleClose}
        heading={ts.locationDescriptionModalHeading()}
        actions={[
          {
            content: ts.system("cancel"),
            onClick: onClose,
            appearance: "outline",
          },
          {
            content: ts.system("next"),
            onClick: () => {
              if (selectedPartnership?.locationDescriptorKey === "MEXP") {
                setCurrentModal(EModals.Intro);
                return;
              }

              setCurrentModal(EModals.Form);
            },
            appearance: "primary",
          },
        ]}
      >
        <Select
          label={getLocalizedText(
            "location_description.modal.field.select_partner",
          )}
          id="select_partner"
          onChange={(option: { label: string; value: number }) => {
            setSelectedPartnershipAccountId(option.value);
          }}
          value={selectedPartnershipAccountId || -1}
          options={(partnerships || []).map(partnership => ({
            value: partnership.partnerAccountId,
            label: getName(partnership.partnerAccount),
          }))}
        />
      </Modal>
    );
  };

  const renderMexpIntroModal = () => {
    return (
      <Modal
        width="medium"
        onClose={handleClose}
        heading={ts.locationDescriptionModalHeading()}
        actions={[
          {
            content: ts.system("cancel"),
            onClick: onClose,
            appearance: "outline",
          },
          {
            content: ts.system("next"),
            onClick: () => setCurrentModal(EModals.Form),
            appearance: "primary",
          },
        ]}
      >
        {ts.locationDescriptionIntroModalContent()}
        <Button onClick={handlePriceListClick} appearance="link">
          {ts.locationDescriptionIntroModalAction()}
        </Button>
      </Modal>
    );
  };

  const renderMoveServiceModal = () => {
    return (
      <RequestRentioMoveServiceModal
        property={property}
        contract={contract}
        onClose={handleClose}
        energyTransferByMexp={true}
      />
    );
  };

  const renderFormModal = () => {
    if (!selectedPartnership) {
      onClose();
      return null;
    }

    return (
      <MultiStepForm
        formId={`index-contact-${formId}`}
        schemas={orderLocationDescriptionSchemas({
          partnership: selectedPartnership,
          property,
          currentContract: contract,
          hasRentioMoveServiceEnabled,
        })}
        asModal={true}
        withAside={false}
        modalProps={{
          onClose,
          heading: ts.locationDescriptionModalHeading(),
          width: "medium",
        }}
        modalActions={[
          {
            content: ts.system("previous"),
            appearance: "outline",
            onClick: () => {
              setCurrentModal(genInitialModal(partnerships));
            },
          },
        ]}
        isPerformingSubmitActionSelector={isPerformingSubmitActionSelector()}
        performingSubmitActionResultSelector={performingSubmitActionResultSelector()}
        performingSubmitActionErrorSelector={performingSubmitActionErrorSelector()}
        submitActionCreator={submitActionCreator({
          partnership: selectedPartnership,
          property,
          currentContract: contract,
        })}
        onSuccess={(_, formData) => handleSuccess(formData)}
        submitLabel={ts.locationDescriptionFormModalAction()}
      />
    );
  };

  const renderOutroModal = () => {
    return (
      <SuccessModal
        onClose={handleCloseOutroModal}
        actions={
          [
            createdTaskId && {
              content: ts.locationDescriptionModalViewTaskAction(),
              url: `/tasks/${createdTaskId}`,
              appearance: "outline",
            },
            {
              content: ts.system("close"),
              onClick: handleCloseOutroModal,
              appearance: "primary",
            },
          ].filter(Boolean) as any
        }
      >
        {ts.locationDescriptionOutroModalContent({
          partner: selectedPartnership?.partnerAccount
            ? getName(selectedPartnership.partnerAccount)
            : "MEXP",
        })}
      </SuccessModal>
    );
  };

  switch (currentModal) {
    case EModals.PartnerPicker:
      return renderPartnerPickerModal();

    case EModals.Form:
      return renderFormModal();

    case EModals.Outro:
      return renderOutroModal();

    case EModals.Intro:
      return renderMexpIntroModal();

    case EModals.EnergyTransfer:
      return renderMoveServiceModal();

    default:
      return null;
  }
};
